import { useState, useEffect, useMemo } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getDuplicateStaff, GetDuplicateStaffOutputType, resolveDuplicate, getDataQuality, GetDataQualityOutputType } from 'zite-endpoints-sdk';
import { isOwnerOrAdmin } from '../lib/roles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Users, Search, CheckCircle2, XCircle, FileWarning, RefreshCw } from 'lucide-react';

type DupItem = GetDuplicateStaffOutputType['duplicates'][0];
type QualityIssue = GetDataQualityOutputType['issues'][0];

function DuplicatesTab() {
  const [data, setData] = useState<GetDuplicateStaffOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [resolveItem, setResolveItem] = useState<DupItem | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await getDuplicateStaff({});
    setData(res);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.duplicates.filter(d => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return d.staffA.name.toLowerCase().includes(q) || d.staffB.name.toLowerCase().includes(q) || d.matchSummary.toLowerCase().includes(q);
      }
      return true;
    });
  }, [data, search, statusFilter]);

  const handleResolve = async (action: 'dismiss' | 'resolve') => {
    if (!resolveItem) return;
    setSaving(true);
    await resolveDuplicate({ reviewId: resolveItem.id, action, notes });
    toast.success(`Duplicate ${action === 'dismiss' ? 'dismissed' : 'resolved'}`);
    setSaving(false);
    setResolveItem(null);
    setNotes('');
    load();
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  const pendingCount = data?.duplicates.filter(d => d.status === 'Pending').length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{pendingCount} pending reviews · {data?.newDetected || 0} newly detected</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Re-scan</Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Dismissed">Dismissed</SelectItem>
            <SelectItem value="Resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
          <p className="font-medium">No duplicates found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => (
            <div key={d.id} className="border border-border rounded-lg p-4 bg-card space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{d.matchSummary}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-[10px]">{d.matchType}</Badge>
                    <span>{d.staffA.name} ({d.staffA.email})</span>
                    <span>↔</span>
                    <span>{d.staffB.name} ({d.staffB.email})</span>
                  </div>
                </div>
                <Badge variant="outline" className={d.status === 'Pending' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : d.status === 'Dismissed' ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/15 text-emerald-400'}>{d.status}</Badge>
              </div>
              {d.status === 'Pending' && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => { setResolveItem(d); setNotes(''); }}>Dismiss / Resolve</Button>
                </div>
              )}
              {d.resolutionNotes && <p className="text-xs text-muted-foreground">Notes: {d.resolutionNotes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!resolveItem} onOpenChange={() => setResolveItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolve Duplicate: {resolveItem?.matchSummary}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Records are never auto-merged. Choose dismiss (false positive) or resolve (manually handled).</p>
          <Textarea placeholder="Resolution notes..." value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleResolve('dismiss')} disabled={saving}>Dismiss</Button>
            <Button onClick={() => handleResolve('resolve')} disabled={saving}>Mark Resolved</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QualityWorklistTab() {
  const [data, setData] = useState<GetDataQualityOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('all');

  useEffect(() => {
    getDataQuality({ severityFilter: severity as any }).then(r => { setData(r); setLoading(false); });
  }, [severity]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search) return data.issues;
    const q = search.toLowerCase();
    return data.issues.filter(i => i.staffName.toLowerCase().includes(q) || i.field.toLowerCase().includes(q));
  }, [data, search]);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Critical" count={data?.summary.critical || 0} color="text-destructive" />
        <StatCard label="Warning" count={data?.summary.warning || 0} color="text-amber-400" />
        <StatCard label="Info" count={data?.summary.info || 0} color="text-blue-400" />
        <StatCard label="Staff Affected" count={data?.summary.staffAffected || 0} color="text-foreground" />
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search staff or field..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        {filtered.slice(0, 100).map((issue, idx) => (
          <div key={idx} className="flex items-center gap-3 px-3 py-2 rounded border border-border bg-card text-sm">
            {issue.severity === 'critical' ? <XCircle className="w-4 h-4 text-destructive shrink-0" /> :
             issue.severity === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" /> :
             <FileWarning className="w-4 h-4 text-blue-400 shrink-0" />}
            <span className="font-medium text-foreground w-32 truncate">{issue.staffName}</span>
            <Badge variant="outline" className="text-[10px]">{issue.field}</Badge>
            <span className="text-muted-foreground flex-1">{issue.message}</span>
            <span className="text-[10px] text-muted-foreground">{issue.department} · {issue.role}</span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            <p className="font-medium">All clear — no data quality issues</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card">
      <div className={`text-2xl font-bold ${color}`}>{count}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

export default function DataQuality() {
  const { user } = useAuth();

  if (!isOwnerOrAdmin(user?.role) && !['Manager'].includes(user?.role || '')) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-2">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">Manager access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Data Quality & Duplicate Detection</h1>
        <p className="text-sm text-muted-foreground mt-1">Staff records with missing data, potential duplicates, and data health issues</p>
      </div>

      <Tabs defaultValue="quality">
        <TabsList>
          <TabsTrigger value="quality" className="gap-1.5"><FileWarning className="w-3.5 h-3.5" /> Worklist</TabsTrigger>
          {isOwnerOrAdmin(user?.role) && <TabsTrigger value="duplicates" className="gap-1.5"><Users className="w-3.5 h-3.5" /> Duplicates</TabsTrigger>}
        </TabsList>
        <TabsContent value="quality" className="mt-4"><QualityWorklistTab /></TabsContent>
        {isOwnerOrAdmin(user?.role) && <TabsContent value="duplicates" className="mt-4"><DuplicatesTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
